import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  useForgotPasswordMutation,
  useResetPasswordMutation,
} from '../../mutations/authMutations';

/**
 * ForgotPasswordModal — Bootstrap 5 Modal for Forgot & Reset Password flow.
 *
 * Step 1: Request OTP / Reset link via POST /api/Auth/forgot-password { emailId }
 * Step 2: Reset Password via POST /api/Auth/reset-password { emailId, otp, newPassword, confirmPassword }
 */
const ForgotPasswordModal = ({ isOpen, onClose, initialEmail = '', isImporter = false }) => {
  const [step, setStep] = useState(1); // 1 = Forgot Password, 2 = Reset Password
  const [submittedEmail, setSubmittedEmail] = useState('');

  const forgotPasswordMutation = useForgotPasswordMutation();
  const resetPasswordMutation = useResetPasswordMutation();

  // Form 1: Forgot Password (Email only)
  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    formState: { errors: errorsForgot },
    reset: resetForgotForm,
    setValue: setForgotValue,
  } = useForm({
    defaultValues: { emailId: '' },
  });

  // Form 2: Reset Password
  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    watch: watchReset,
    formState: { errors: errorsReset },
    reset: resetResetForm,
    setValue: setResetValue,
  } = useForm({
    defaultValues: {
      emailId: '',
      otp: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  // Sync initialEmail when modal opens or initialEmail changes
  useEffect(() => {
    if (isOpen) {
      setForgotValue('emailId', initialEmail || '');
      setResetValue('emailId', initialEmail || '');
      setSubmittedEmail(initialEmail || '');
    } else {
      // Reset state when closed
      setStep(1);
      resetForgotForm();
      resetResetForm();
    }
  }, [isOpen, initialEmail, setForgotValue, setResetValue, resetForgotForm, resetResetForm]);

  if (!isOpen) return null;

  const handleCloseModal = () => {
    setStep(1);
    resetForgotForm();
    resetResetForm();
    onClose();
  };

  // ── Step 1 Submit: Forgot Password ──────────────────────────────────────────
  const onForgotSubmit = (data) => {
    const payload = { isImporter, emailId: data.emailId?.trim() };

    forgotPasswordMutation.mutate(payload, {
      onSuccess: () => {
        setSubmittedEmail(data.emailId?.trim());
        setResetValue('emailId', data.emailId?.trim());
        setStep(2); // Move to Reset Password step
      },
    });
  };

  // ── Step 2 Submit: Reset Password ───────────────────────────────────────────
  const onResetSubmit = (data) => {
    const payload = {
      isImporter,
      emailId: data.emailId?.trim(),
      otp: data.otp?.trim(),
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword,
    };

    resetPasswordMutation.mutate(payload, {
      onSuccess: () => {
        handleCloseModal();
      },
    });
  };

  const newPasswordValue = watchReset('newPassword');

  return (
    <>
      {/* Bootstrap 5 Modal Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={handleCloseModal}
      />

      {/* Bootstrap 5 Modal */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="forgotPasswordModalLabel"
        aria-hidden="false"
        style={{ zIndex: 1055 }}
        onClick={handleCloseModal}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-sm"
          style={{ maxWidth: '420px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content border-0 shadow-lg rounded-3">
            {/* Header */}
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold text-dark" id="forgotPasswordModalLabel">
                {step === 1 ? 'Forgot Password' : 'Reset Password'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close"
              />
            </div>

            {/* Body */}
            <div className="modal-body px-4 pt-3 pb-4">
              {step === 1 ? (
                /* ── STEP 1: Forgot Password Form ── */
                <form onSubmit={handleSubmitForgot(onForgotSubmit)} noValidate>
                  <p className="text-muted small mb-3">
                    Enter your email address below. We'll send you an OTP to reset your password.
                  </p>

                  <div className="mb-3">
                    <label htmlFor="fp-email" className="form-label small fw-semibold text-secondary">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      id="fp-email"
                      type="email"
                      className={`form-control form-control-sm ${errorsForgot.emailId ? 'is-invalid' : ''
                        }`}
                      placeholder="e.g. user@example.com"
                      autoComplete="email"
                      {...registerForgot('emailId', {
                        required: 'Email address is required.',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email address.',
                        },
                      })}
                    />
                    {errorsForgot.emailId && (
                      <div className="invalid-feedback small">
                        {errorsForgot.emailId.message}
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary px-3"
                      onClick={handleCloseModal}
                      disabled={forgotPasswordMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-sm btn-primary px-4"
                      disabled={forgotPasswordMutation.isPending}
                    >
                      {forgotPasswordMutation.isPending ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          />
                          Sending...
                        </>
                      ) : (
                        'Reset'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                /* ── STEP 2: Reset Password Form ── */
                <form onSubmit={handleSubmitReset(onResetSubmit)} noValidate>
                  <p className="text-muted small mb-3">
                    An OTP has been sent to <strong>{submittedEmail}</strong>. Enter the OTP and your new password below.
                  </p>

                  {/* Email ID (editable or prefilled) */}
                  <div className="mb-2">
                    <label htmlFor="rp-email" className="form-label small fw-semibold text-secondary">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      id="rp-email"
                      type="email"
                      className={`form-control form-control-sm ${errorsReset.emailId ? 'is-invalid' : ''
                        }`}
                      placeholder="Email address"
                      {...registerReset('emailId', {
                        required: 'Email address is required.',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email address.',
                        },
                      })}
                    />
                    {errorsReset.emailId && (
                      <div className="invalid-feedback small">
                        {errorsReset.emailId.message}
                      </div>
                    )}
                  </div>

                  {/* OTP */}
                  <div className="mb-2">
                    <label htmlFor="rp-otp" className="form-label small fw-semibold text-secondary">
                      OTP <span className="text-danger">*</span>
                    </label>
                    <input
                      id="rp-otp"
                      type="text"
                      className={`form-control form-control-sm ${errorsReset.otp ? 'is-invalid' : ''
                        }`}
                      placeholder="Enter OTP"
                      autoComplete="one-time-code"
                      {...registerReset('otp', {
                        required: 'OTP is required.',
                      })}
                    />
                    {errorsReset.otp && (
                      <div className="invalid-feedback small">{errorsReset.otp.message}</div>
                    )}
                  </div>

                  {/* New Password */}
                  <div className="mb-2">
                    <label htmlFor="rp-newPassword" className="form-label small fw-semibold text-secondary">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <input
                      id="rp-newPassword"
                      type="password"
                      className={`form-control form-control-sm ${errorsReset.newPassword ? 'is-invalid' : ''
                        }`}
                      placeholder="Enter new password"
                      {...registerReset('newPassword', {
                        required: 'New password is required.',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters.',
                        },
                      })}
                    />
                    {errorsReset.newPassword && (
                      <div className="invalid-feedback small">
                        {errorsReset.newPassword.message}
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="mb-3">
                    <label htmlFor="rp-confirmPassword" className="form-label small fw-semibold text-secondary">
                      Confirm Password <span className="text-danger">*</span>
                    </label>
                    <input
                      id="rp-confirmPassword"
                      type="password"
                      className={`form-control form-control-sm ${errorsReset.confirmPassword ? 'is-invalid' : ''
                        }`}
                      placeholder="Re-enter new password"
                      {...registerReset('confirmPassword', {
                        required: 'Please confirm your password.',
                        validate: (value) =>
                          value === newPasswordValue || 'Passwords do not match.',
                      })}
                    />
                    {errorsReset.confirmPassword && (
                      <div className="invalid-feedback small">
                        {errorsReset.confirmPassword.message}
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      onClick={() => setStep(1)}
                      disabled={resetPasswordMutation.isPending}
                    >
                      ← Back
                    </button>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary px-3"
                        onClick={handleCloseModal}
                        disabled={resetPasswordMutation.isPending}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-sm btn-primary px-3"
                        disabled={resetPasswordMutation.isPending}
                      >
                        {resetPasswordMutation.isPending ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            />
                            Resetting...
                          </>
                        ) : (
                          'Reset Password'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordModal;
